import React, { useEffect, useMemo, useRef, useState } from 'react';

import { toProjectDateOnly, toUtcDeadline } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import { useWbsOptions } from 'src/features/tasks/hooks/useWbsOptions';

import Modal from 'src/components/common/ui/Modal';
import type { Risk } from '../types/risk.type';
import { calcScore, ragFromScore } from 'src/lib/riskCalc';
// import { useCreateRisk } from '../hooks/useCreateRisk';
// import { useUpdateRisk } from '../hooks/useUpdateRisk';
// import { useDeleteRisk } from '../hooks/useDeleteRisk';
import { useRiskCategories } from '../hooks/useRiskCategories';
import { useProjectMembers } from '../hooks/useProjectMembers';
import RiskOwnerSelect from './RiskOwnerSelect';

import type { CreateRiskInput, UpdateRiskPatch } from '../api/risks.api';

type Props = {
    open: boolean;
    onClose: () => void;
    projectId: string;
    risk?: Risk | null;
    canEdit?: boolean;
    onSaved?: (saved: Risk) => void;
    onDeleted?: (deletedId: string) => void;
    currentUserId?: string;
    userNameMap?: Record<string, string>;

    // ✅ DB 연동용 콜백
    onCreate: (input: CreateRiskInput) => Promise<Risk>;
    onUpdate: (taskId: string, patch: UpdateRiskPatch) => Promise<Risk>;
    onDelete: (taskId: string) => Promise<any>;

    fixedWbsNodeId?: string;
};

const ragPill = (rag?: Risk['rag'] | null) =>
    rag === 'R'
        ? 'bg-rose-100 text-rose-700'
        : rag === 'A'
          ? 'bg-amber-100 text-amber-700'
          : rag === 'G'
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-gray-100 text-gray-700';

export default function RiskEditorModal({
    open,
    onClose,
    projectId,
    risk,
    canEdit,
    onSaved,
    onDeleted,
    currentUserId,
    userNameMap,
    onCreate,
    onUpdate,
    onDelete,
    fixedWbsNodeId,
}: Props) {
    const isEdit = !!risk?.id;
    const focusRef = useRef<HTMLInputElement>(null);

    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const { data: categories = [] } = useRiskCategories(projectId, open);
    const { data: members = [], isLoading: membersLoading } = useProjectMembers(projectId, open);

    const needWbsSelect = open && !!fixedWbsNodeId;
    const { options: wbsOptions, isLoading: wbsLoading } = useWbsOptions(projectId, needWbsSelect);

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [probability, setProbability] = useState<number | null>(null);
    const [impact, setImpact] = useState<number | null>(null);
    const [dueDate, setDueDate] = useState<string>('');
    const [mitigationPlan, setMitigationPlan] = useState<string>('');
    const [contingencyPlan, setContingencyPlan] = useState<string>('');
    const [weeklyNote, setWeeklyNote] = useState<string>('');
    const [nextReviewAt, setNextReviewAt] = useState<string>('');
    const [status, setStatus] = useState<'OPEN' | 'CLOSED'>('OPEN');
    const [closedNote, setClosedNote] = useState<string>('');
    const [wbsNodeId, setWbsNodeId] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;

        if (risk) {
            setTitle(risk.title ?? '');
            setCategory(risk.category ?? '');
            setDescription(risk.description ?? '');
            setProbability(typeof risk.probability === 'number' ? risk.probability : null);
            setImpact(typeof risk.impact === 'number' ? risk.impact : null);
            // setDueDate(risk.dueDate ?? '');
            setDueDate(risk.dueDate ? toProjectDateOnly(risk.dueDate, zone) : '');
            setMitigationPlan(risk.mitigationPlan ?? '');
            setContingencyPlan(risk.contingencyPlan ?? '');
            setWeeklyNote(risk.weeklyNote ?? '');
            // setNextReviewAt(risk.nextReviewAt ?? '');
            setNextReviewAt(risk.nextReviewAt ? toProjectDateOnly(risk.nextReviewAt, zone) : '');
            setStatus((risk.status as 'OPEN' | 'CLOSED') ?? 'OPEN');
            setClosedNote(risk.closedNote ?? '');
            // ✅ 기존 risk.owner 우선, 없으면 createdBy, 없으면 currentUserId
            setOwnerUserId(risk.ownerUserId ?? risk.createdByUserId ?? currentUserId ?? null);
            setWbsNodeId(fixedWbsNodeId ?? risk.wbsNodeId ?? null);
        } else {
            setTitle('');
            setCategory('');
            setDescription('');
            setProbability(null);
            setImpact(null);
            setDueDate('');
            setMitigationPlan('');
            setContingencyPlan('');
            setWeeklyNote('');
            setNextReviewAt('');
            setStatus('OPEN');
            setClosedNote('');
            // ✅ Task처럼 기본 owner를 currentUserId로
            setOwnerUserId(currentUserId ?? null);
            setWbsNodeId(fixedWbsNodeId ?? null);
        }

        setSaving(false);
        setDeleting(false);
    }, [open, risk, currentUserId, zone, fixedWbsNodeId]);

    const score = useMemo(() => calcScore(probability, impact), [probability, impact]);
    const rag = useMemo(() => ragFromScore(score), [score]);

    const canSave = canEdit && title.trim().length > 0 && !saving && !deleting;

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);

        try {
            // ✅ WBS 연결값은 fixedWbsNodeId가 최우선
            // const resolvedWbsNodeId = fixedWbsNodeId ?? risk?.wbsNodeId ?? null;
            const resolvedWbsNodeId = fixedWbsNodeId ?? wbsNodeId; // null이면 null 저장(해제)

            if (isEdit && risk) {
                const patch: UpdateRiskPatch = {
                    title: title.trim(),
                    category: category.trim() || null,
                    description: description.trim() || null,

                    probability,
                    impact,
                    score: score ?? null,
                    rag: rag ?? null,

                    dueDate: dueDate || null,
                    mitigationPlan: mitigationPlan.trim() || null,
                    contingencyPlan: contingencyPlan.trim() || null,
                    weeklyNote: weeklyNote.trim() || null,
                    nextReviewAt: nextReviewAt || null,

                    status,
                    closedNote: status === 'CLOSED' ? closedNote.trim() || null : null,

                    // ✅ update에서도 기존 wbsNodeId 유지(고정이면 고정)
                    wbsNodeId: resolvedWbsNodeId,

                    // ✅ owner 정책: 선택값이 있으면 반영
                    ownerUserId: ownerUserId ?? null,
                };
                const saved = await onUpdate(risk.id, patch);
                onSaved?.(saved);
                onClose();
                return;
            }
            const input: CreateRiskInput = {
                title: title.trim(),
                category: category.trim() || null,
                description: description.trim() || null,

                probability,
                impact,
                score: score ?? null,
                rag: rag ?? null,

                dueDate: dueDate || null,
                mitigationPlan: mitigationPlan.trim() || null,
                contingencyPlan: contingencyPlan.trim() || null,
                weeklyNote: weeklyNote.trim() || null,
                nextReviewAt: nextReviewAt || null,

                status,
                closedNote: status === 'CLOSED' ? closedNote.trim() || null : null,

                // ✅ create에서 WBS 연결 확정
                wbsNodeId: resolvedWbsNodeId,

                // ✅ ownerUserId가 null이면 서버 default(createdBy) 사용
                ...(ownerUserId ? { ownerUserId } : {}),
            };
            const saved = await onCreate(input);
            onSaved?.(saved);
            onClose();
        } catch (err: any) {
            // const status = err?.response?.status;
            // if (status === 403) return;
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEdit || !risk || !canEdit) return;
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('정말 삭제하시겠습니까?')) return;

        setDeleting(true);
        try {
            await onDelete(risk.id);
            onDeleted?.(risk.id);
            onClose();
        } catch (err: any) {
            // const status = err?.response?.status;
            // if (status === 403) return;
            throw err;
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? `Risk ${risk?.id.slice(0, 8)} 상세` : '새 Risk 생성'}
            size="5xl"
            initialFocusRef={focusRef}
            closeOnBackdrop={false}
        >
            <div className="text-sm space-y-4">
                {/* 기본정보 */}
                {!fixedWbsNodeId && (
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">WBS</label>
                        <select
                            className="w-full rounded border px-2 py-1"
                            value={wbsNodeId ?? ''}
                            disabled={!canEdit || wbsLoading}
                            onChange={(e) => setWbsNodeId(e.target.value || null)}
                        >
                            <option value="">(미연결)</option>
                            {wbsOptions.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        {wbsLoading && <div className="mt-1 text-[11px] text-gray-400">Loading WBS...</div>}
                    </div>
                )}
                <div className="flex gap-3">
                    <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-1">제목 *</label>
                        <input
                            ref={focusRef}
                            className="w-full rounded border px-2 py-1"
                            value={title}
                            disabled={!canEdit}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="예: 전원반 입고 지연"
                        />
                    </div>

                    <div className="w-64">
                        <label className="block text-xs text-gray-600 mb-1">카테고리</label>
                        <input
                            className="w-full rounded border px-2 py-1"
                            list="risk-categories"
                            value={category}
                            disabled={!canEdit}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder="예: 공급망"
                        />
                        <datalist id="risk-categories">
                            {categories.map((c) => (
                                <option key={c} value={c} />
                            ))}
                        </datalist>
                    </div>

                    <RiskOwnerSelect
                        members={members}
                        value={ownerUserId}
                        disabled={!canEdit || membersLoading}
                        onChange={setOwnerUserId}
                        createdByUserId={risk?.createdByUserId ?? null}
                    />
                </div>

                <div>
                    <label className="block text-xs text-gray-600 mb-1">상세설명</label>
                    <textarea
                        rows={3}
                        className="w-full rounded border px-2 py-1"
                        value={description}
                        disabled={!canEdit}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-4 gap-2">
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Probability (1~5)</label>
                        <select
                            className="w-full rounded border px-2 py-1"
                            disabled={!canEdit}
                            value={probability ?? ''}
                            onChange={(e) => setProbability(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">-</option>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Impact (1~5)</label>
                        <select
                            className="w-full rounded border px-2 py-1"
                            disabled={!canEdit}
                            value={impact ?? ''}
                            onChange={(e) => setImpact(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">-</option>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Score</label>
                            <input
                                className="w-full rounded border px-2 py-1 bg-gray-50"
                                value={score ?? ''}
                                disabled
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">RAG</label>
                            <div className={`inline-flex items-center px-2 py-1 rounded ${ragPill(rag)}`}>
                                {rag ?? '—'}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                        <input
                            type="date"
                            className="w-full rounded border px-2 py-1"
                            value={dueDate || ''}
                            disabled={!canEdit}
                            onChange={(e) => setDueDate(e.target.value || '')}
                        />
                    </div>
                </div>

                {/* ✅ 계획/노트 */}
                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3"> */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Mitigation Plan</label>
                    <textarea
                        rows={5}
                        className="w-full rounded border px-2 py-1"
                        value={mitigationPlan}
                        disabled={!canEdit}
                        onChange={(e) => setMitigationPlan(e.target.value)}
                        placeholder="예: 대체 공급처 확보, 납기 단축 옵션 검토"
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Contingency Plan</label>
                    <textarea
                        rows={5}
                        className="w-full rounded border px-2 py-1"
                        value={contingencyPlan}
                        disabled={!canEdit}
                        onChange={(e) => setContingencyPlan(e.target.value)}
                        placeholder="예: 일정 재배치, 예산/리소스 재배분"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Weekly Note</label>
                    <textarea
                        rows={3}
                        className="w-full rounded border px-2 py-1"
                        value={weeklyNote}
                        disabled={!canEdit}
                        onChange={(e) => setWeeklyNote(e.target.value)}
                        placeholder="예: 이번 주 커뮤니케이션/이슈/결정사항"
                    />
                </div>

                <div className="w-64">
                    <label className="block text-xs text-gray-600 mb-1">Next Review</label>
                    <input
                        type="date"
                        className="w-full rounded border px-2 py-1"
                        value={nextReviewAt || ''}
                        disabled={!canEdit}
                        onChange={(e) => setNextReviewAt(e.target.value || '')}
                    />
                </div>
                {/* </div> */}

                {/* 상태 */}
                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            className="accent-gray-900"
                            checked={status === 'CLOSED'}
                            disabled={!canEdit}
                            onChange={(e) => setStatus(e.target.checked ? 'CLOSED' : 'OPEN')}
                        />
                        <span>Mark as Resolved (Closed)</span>
                    </label>
                </div>

                {status === 'CLOSED' && (
                    <div>
                        <label className="block text-xs text-gray-600 mb-1">Closed Note</label>
                        <textarea
                            rows={2}
                            className="w-full rounded border px-2 py-1"
                            value={closedNote}
                            disabled={!canEdit}
                            onChange={(e) => setClosedNote(e.target.value)}
                        />
                    </div>
                )}

                <div className="flex justify-between pt-2">
                    <div>
                        {isEdit && (
                            <button
                                type="button"
                                className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50 text-rose-600 disabled:opacity-50"
                                onClick={handleDelete}
                                disabled={!canEdit}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="text-xs rounded px-2.5 py-1 text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50"
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
